<template>
  <button
    :aria-describedby="ariaDescribedby"
    :aria-label="ariaLabel"
    :aria-labelledby="ariaLabeledby"
    :disabled="disabled"
    :type="submit ? 'submit' : 'button'"
    :class="['button', themeClass, state, classes]"
    :role="'button'"
    :style="{ backgroundColor: backgroundColor, color: color }"
  >
    <slot />

    <template v-if="hasIcon">
      <span class="icon-wrapper">
        <slot name="icon" />
      </span>
      <span class="icon-wrapper-sm">
        <slot name="icon-sm" />
      </span>
    </template>

    <template v-if="!hasIcon && secondary">
      <span class="icon-wrapper">
        <IconChevron aria-hidden="true" />
      </span>
      <span class="icon-wrapper-sm">
        <IconChevronSm aria-hidden="true" />
      </span>
    </template>
  </button>
</template>

<script>
import IconChevron from '../assets/icons/chevron.svg'
import IconChevronSm from '../assets/icons/chevronSm.svg'
/**
 * `Buttons` are generally used for interface actions. Suitable for all-purpose use.
 * Default appearance set to `primary` style.
 * `primary` style will be used only once per view for main call-to-action.
 */
export default {
  name: 'WvcButton',
  components: {
    IconChevron,
    IconChevronSm
  },
  props: {
    ariaDescribedby: {
      type: String,
      default: null
    },
    ariaLabel: {
      type: String,
      default: null
    },
    ariaLabeledby: {
      type: String,
      default: null
    },
    /**
     * Use dark theme for component.
     */
    dark: {
      type: Boolean,
      default: false
    },
    /**
     * If set, overrides the default light/dark theme's color for the button background color.
     */
    backgroundColor: {
      type: String,
      default: null
    },
    /**
     * If set, overrides the default light/dark theme's text color for the button.
     */
    color: {
      type: String,
      default: null
    },
    disabled: {
      type: Boolean,
      default: false
    },
    fullWidth: {
      type: Boolean,
      default: false
    },
    /**
     * Use the secondary styling for the button
     */
    secondary: {
      type: Boolean,
      default: false
    },
    /**
     * Manually trigger various states of the button.
     * @values hover, active, focus
     */
    state: {
      type: String,
      default: null,
      validator: value => {
        return value.match(/(hover|focus|active|visited|disabled)/)
      }
    },
    /**
     * Set the button’s type to “submit”.
     */
    submit: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    classes() {
      return {
        'button-primary': !this.secondary,
        'button-secondary': this.secondary,
        'full-width': this.fullWidth,
        'button-submit-disabled': this.disabled && this.submit
      }
    },
    // TODO - can be refactored out of all components as shared module once we are using Vue 3 composition API
    // TODO - can also break out things like size props + classes, etc.
    themeClass() {
      return {
        'theme-light': !this.dark,
        'theme-dark': this.dark
      }
    },
    hasIcon() {
      return this.$slots.icon
    }
  }
}
</script>

<style lang="scss" scoped>
.button {
  @include reset;
  will-change: transform;
  transition: all 0.2s ease;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  font-weight: $weight-semi-bold;
  font-size: $size-m;
  font-family: $font-heading;
  @include lhCrop(1.2);
  text-transform: uppercase;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: $radius-default;
  cursor: pointer;

  //  Large button size (default)
  @include typographylargelinkbtnlabel;
  padding: $button-large-padding;

  //  Small Button style
  @include media('handheld') {
    @include typographysmalllinkbtnlabel;
    padding: $button-small-padding;
  }

  %icon-wrapper-base {
    padding-left: 8px;
    overflow: visible;
    align-items: center;
    height: 100%;

    & > svg {
      display: block;
      margin: 0 auto;
      text-align: center;
    }
  }

  .icon-wrapper {
    @extend %icon-wrapper-base;
    display: flex;
    @include media('handheld') {
      display: none;
    }
  }

  .icon-wrapper-sm {
    @extend %icon-wrapper-base;
    display: none;
    @include media('handheld') {
      display: flex;
    }
  }
}

.button,
.button-primary {
  @include themify($themes) {
    color: themed('text-secondary');
    background-color: themed('primary');
    box-shadow: none;
    border: none;
    &:hover,
    &.hover {
      color: themed('text-emphasis');
      background-color: themed('emphasis');
      box-shadow: none;
    }
    &:focus,
    &.focus {
      background-color: themed('emphasis');
      color: themed('text-emphasis');
      box-shadow: themed('button-box-shadow-focus');
      outline: none;
    }
    &:active,
    &.active,
    &:visited,
    &.visited {
      transition: none;
      background-color: themed('active');
      color: $white;
      box-shadow: themed('button-primary-shadow-pressed');
    }
    &:disabled,
    &.disabled {
      background-color: transparent;
      color: themed('inactive-text');
      box-shadow: themed('button-primary-shadow-inactive');
    }
  }
}
.button-secondary {
  @include themify($themes) {
    color: themed('primary');
    background-color: themed('active-secondary');
    box-shadow: themed('button-secondary-shadow-default');
    border: none;
    &:hover,
    &.hover {
      color: themed('emphasis-secondary');
      background-color: $white;
      box-shadow: themed('button-secondary-shadow-hover');
    }
    &:focus,
    &.focus {
      background-color: themed('emphasis');
      color: themed('text-emphasis-secondary');
      box-shadow: themed('button-box-shadow-focus');
      outline: none;
    }
    &:active,
    &.active {
      transition: none;
      background-color: themed('active-secondary');
      color: themed('secondary');
      box-shadow: themed('button-secondary-shadow-pressed');
    }
    &:disabled,
    &.disabled {
      background-color: transparent;
      color: themed('inactive-text');
      box-shadow: themed('button-secondary-shadow-inactive');
    }
  }
  text-align: left;
}
.full-width {
  width: 100%;
}
.button-submit-disabled {
  @include themify($themes) {
    color: themed('button-submit-disabled-color') !important;
    background-color: themed('button-submit-disabled-background') !important;
    box-shadow: themed('button-submit-disabled-shadow') !important;
  }
}
</style>
